<template>
    <div class="login-section">
        <div class="text-center mb-5">
            <img src="@/assets/logo1.png" height="100" alt="Logo" loading="lazy" />
        </div>
        <div class="d-flex justify-content-center align-items-center">
            <div class="card-perso p-3">
                <p class="auth-title my-3 h2 font-weight-bold">CONNEXION</p>

                <!-- Étape 1: Email -->
                <div v-if="currentStep === 1" class="step" :class="{ 'fade-in': step1Active }">
                    <form @submit.prevent="verifyEmail">
                        <div class="form-group">
                            <div class="input-group">
                                <div class="input-group-prepend">
                                    <span class="input-group-text"><i class="fas fa-envelope"></i></span>
                                </div>
                                <input type="email" class="form-control form-control-lg" id="email" v-model="email"
                                    required placeholder="Entrez votre email">
                            </div>
                        </div>
                        <button type="submit" class="btn btn-lg btn-block btn-primary" :disabled="loading">
                            <span v-if="loading">
                                <i class="fas fa-spinner fa-spin"></i> Vérification...
                            </span>
                            <span v-else>Continuer</span>
                        </button>
                    </form>
                </div>

                <!-- Étape 2: Code de vérification moderne -->
                <div v-if="currentStep === 2" class="step" :class="{ 'fade-in': step2Active }">
                    <form @submit.prevent="verifyCode">
                        <div class="alert alert-info">
                            Un code de vérification a été envoyé à <strong>{{ email }}</strong>.
                            Il expirera dans 10 minutes.
                        </div>

                        <div class="form-group">
                            <label>Code de vérification</label>
                            <div class="d-flex justify-content-between mb-3">
                                <input v-for="(digit, index) in codeDigits" :key="index" type="text"
                                    class="form-control verification-digit text-center mx-1" v-model="codeDigits[index]"
                                    maxlength="1" pattern="[0-9]" inputmode="numeric"
                                    @input="handleCodeInput(index, $event)"
                                    @keydown.delete="handleBackspace(index, $event)"
                                    @paste.prevent="handlePaste($event)" ref="digitInputs">
                            </div>
                            <small class="text-muted">Entrez le code à 4 chiffres reçu par email</small>
                        </div>

                        <div v-if="codeError" class="alert alert-danger">
                            <i class="fas fa-exclamation-circle"></i> {{ codeError }}
                        </div>

                    </form>
                </div>
            </div>
        </div>
    </div>
</template>

<script>

export default {
    data() {
        return {
            currentStep: 1,
            step1Active: true,
            step2Active: false,
            loading: false,
            email: '',
            codeDigits: ['', '', '', ''],
            codeError: '',
        };
    },
    computed: {
        verificationCode() {
            return this.codeDigits.join('');
        }
    },
    methods: {
        async verifyEmail() {
            this.loading = true;
            try {
                const response = await this.axios.post('/api/authenticate', {
                    email: this.email,
                    source: "mon_espace"
                });

                if (response.data.success == true) this.goToStep(2);
            } catch (error) {
                alert('Une erreur est survenue lors de la vérification de votre email.');
                console.error(error);
            } finally {
                this.loading = false;
            }
        },

        async verifyCode() {
            this.loading = true;
            this.codeError = '';

            try {
                const response = await this.axios.post('/api/verify-code', {
                    email: this.email,
                    verification_code: this.verificationCode
                });

                if (response.data.success === true) {

                    const connexion = response.data.connexion;
                    const user = response.data.user;
                    const token = response.data.token;

                    const userDatas = {
                        user: response.data.user,
                        token: response.data.token
                    }

                    this.axios.defaults.headers.common = {
                        'Authorization': 'Bearer ' + token
                    };

                    this.$store.commit('setAuthenticated', userDatas)

                    this.form = {}

                    if (connexion.status == 'nouveau') {
                        this.$router.push({ name: 'register' })
                    } else {
                        this.$router.push({ name: 'accueil' })
                    }
                }
                else {
                    this.$toast.add({
                        severity: 'warn',
                        detail: response.data.errors,
                        life: 5000
                    });
                }

                this.form.verification_code = ''
                this.sendingCode = false

            } catch (error) {
                this.codeError = 'Une erreur est survenue lors de la vérification du code.';
                console.error(error);
            } finally {
                this.loading = false;
            }
        },

        goToStep(step) {
            if (this.currentStep === 1) this.step1Active = false;
            if (this.currentStep === 2) this.step2Active = false;

            setTimeout(() => {
                this.currentStep = step;
                if (step === 1) this.step1Active = true;
                if (step === 2) {
                    this.step2Active = true;
                    this.$nextTick(() => {
                        if (this.$refs.digitInputs && this.$refs.digitInputs[0]) {
                            this.$refs.digitInputs[0].focus();
                        }
                    });
                }
            }, 300);
        },

        handleCodeInput(index, event) {
            const value = event.target.value;

            // Ne permettre que des chiffres
            if (!/^\d*$/.test(value)) {
                this.codeDigits[index] = '';
                return;
            }

            // Passer au champ suivant si un chiffre est entré
            if (value && index < 3) {
                this.$nextTick(() => {
                    this.$refs.digitInputs[index + 1].focus();
                });
            }

            // Si on a complété tous les chiffres, soumettre automatiquement
            if (this.verificationCode.length === 4) {
                this.verifyCode();
            }
        },

        handleBackspace(index, event) {
            if (event.key === 'Backspace' && !this.codeDigits[index] && index > 0) {
                this.$nextTick(() => {
                    this.$refs.digitInputs[index - 1].focus();
                });
            }
        },

        handlePaste(event) {
            const pasteData = event.clipboardData.getData('text').trim();
            if (/^\d{4}$/.test(pasteData)) {
                this.codeDigits = pasteData.split('');
                this.$nextTick(() => {
                    this.verifyCode();
                });
            }
        },

        resetForm() {
            this.email = '',
                this.codeDigits = ['', '', '', ''],
                this.codeError = ''
        },
    }
};
</script>

<style scoped>
.login-section {
    padding-top: 10%;
}

.card-perso {
    top: 50%;
    left: 50%;
    max-width: 350px;
    max-height: 400px;
    width: 90%;
    /* height: 400px; */
    background: #FFFFFF 0% 0% no-repeat padding-box;
    box-shadow: 0px 3px 6px #0000001A;
    border-radius: 16px;
    opacity: 1;
}

.step {
    transition: all 0.3s ease;
}

.fade-in {
    animation: fadeIn 0.5s;
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(20px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.card {
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.input-group-text {
    min-width: 40px;
    justify-content: center;
}

.btn {
    margin-top: 10px;
}

.verification-digit {
    width: 50px;
    height: 60px;
    font-size: 24px;
    font-weight: bold;
}

.verification-digit:focus {
    border-color: #80bdff;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
    outline: none;
}

.auth-title {
    text-align: center;
    /* font: normal normal normal 18px/180px Altero; */
    letter-spacing: 0.45px;
    color: #0C266C;
    text-transform: uppercase;
    opacity: 1;
}
</style>

    <div class="login-section">
        <div class="text-center mb-5">
            <img src="@/assets/logo1.png" height="100" alt="Logo" loading="lazy" />
        </div>
        <div class="d-flex justify-content-center align-items-center">
            <div class="card-perso p-3">
                <div class="">
                    <p class="auth-title my-3 h2 font-weight-bold">CONNEXION</p>
                    <form @submit.prevent="login(true)" v-if="step == 1">
                        <div class="input-group mb-3">
                            <div class="input-group-prepend">
                                <span class="input-group-text" id="basic-addon1">
                                    <i class="fa-solid fa-user"></i>
                                </span>
                            </div>
                            <input placeholder="Adresse email" type="email" class="form-control form-control-lg "
                                :class="{ 'is-invalid': status === false }" v-model="form.email" required
                                autocomplete="email" autofocus>
                        </div>

                        <div class=" mb-0 text-center">
                            <button type="submit" class="btn btn-lg btn-block btn-primary" :disabled="isLoading">
                                Commencer
                                <span v-if="isLoading == true" class="spinner-border spinner-border-sm" role="status"
                                    aria-hidden="true"></span>
                                <svg v-else xmlns="http://www.w3.org/2000/svg" class="ml-2" width="8" height="10"
                                    viewBox="0 0 8 10" fill="none">
                                    <path
                                        d="M7.84327 4.79413L3.21217 9.14911C3.06502 9.29238 2.88901 9.40668 2.6944 9.48529C2.49979 9.56391 2.29048 9.60515 2.07869 9.60688C1.86689 9.60861 1.65685 9.57073 1.46082 9.4953C1.26479 9.41988 1.08669 9.30851 0.936919 9.16767C0.787151 9.02683 0.668713 8.85934 0.58851 8.67499C0.508307 8.49065 0.467946 8.29311 0.469787 8.09394C0.471627 7.89477 0.51563 7.69809 0.599227 7.51508C0.682825 7.33207 0.804342 7.16639 0.956691 7.02802L3.33233 4.79413L0.956691 2.56098C0.657455 2.27972 0.489293 1.89829 0.489193 1.50043C0.489094 1.10257 0.657065 0.721036 0.956159 0.439641C1.25525 0.158246 1.66097 3.71933e-05 2.08405 -5.62668e-05C2.50713 -0.000149727 2.91293 0.15789 3.21217 0.439153L7.84327 4.79413Z"
                                        fill="#F5BA27" />
                                </svg>
                            </button>

                        </div>
                    </form>

                    <form @submit.prevent="sendCode()" v-if="step == 2">
                        <div class="input-group mb-3 px-4">
                            <div class="input-group-prepend">
                                <span class="input-group-text" id="basic-addon1">
                                    <i class="fa-solid fa-lock"></i>
                                </span>
                            </div>
                            <input placeholder="Code de verification" type="password"
                                class="form-control form-control-lg" :class="{ 'is-invalid': status === false }"
                                v-model="form.verification_code" required @input="checkCode()" :disabled="sendingCode">
                        </div>

                        <div class="mb-3 text-center">
                            <span v-if="sendingCode == true" class="spinner-border spinner-border-sm" role="status"
                                aria-hidden="true"></span>
                        </div>

                        <button type="button" class="btn btn-sm btn-block btn-light" :disabled="isLoading"
                            @click="login(false)">
                            Je n'ai pas reçu de code. Renvoyer maintenant
                            <span v-if="isLoading == true" class="spinner-border spinner-border-sm" role="status"
                                aria-hidden="true"></span>
                        </button>
                    </form>
                </div>
            </div>
        </div>
    </div>
</template>

<script>

export default {
    name: 'LoginView',
    data() {
        return {
            isLoading: false,
            sendingCode: false,
            step: 1,
            form: {
                source: 'mon_espace'
            }
        }
    },
    methods: {
        login(toStep) {
            this.isLoading = true
            this.axios.post('/api/authenticate', this.form).then(response => {
                if (response.data.success === true) {
                    if (toStep == true) {
                        this.step++
                    }
                    this.$toast.add({
                        severity: 'success',
                        detail: response.data.message,
                        life: 3000
                    });
                }
                else {
                    this.$toast.add({
                        severity: 'warn',
                        detail: response.data.errors[0],
                        life: 5000
                    });
                }
                this.isLoading = false
            }).catch(() => {
                this.isLoading = false
                this.$toast.add({
                    severity: 'error',
                    summary: 'Probleme de connexion',
                    detail: 'Une erreur s\'est produite lors de la connexion au serveur !',
                    life: 5000
                });
            })
        },
        sendCode() {
            this.sendingCode = true
            this.axios.post('/api/verify-code', this.form).then(response => {
                if (response.data.success === true) {

                    const connexion = response.data.connexion;
                    const user = response.data.user;
                    const token = response.data.token;

                    const userDatas = {
                        user: response.data.user,
                        token: response.data.token
                    }

                     this.axios.defaults.headers.common = {
                         'Authorization': 'Bearer ' + token
                     };

                    this.$store.commit('setAuthenticated', userDatas)

                    this.form = {}

                    if (connexion.status == 'nouveau') {
                        this.$router.push({ name: 'register' })
                    } else {
                        this.$router.push({ name: 'accueil' })
                    }
                }
                else {
                    this.$toast.add({
                        severity: 'warn',
                        detail: response.data.errors,
                        life: 5000
                    });
                }

                this.form.verification_code = ''
                this.sendingCode = false
            }).catch(() => {
                this.sendingCode = false
                this.$toast.add({
                    severity: 'error',
                    summary: 'Probleme de connexion',
                    detail: 'Une erreur s\'est produite lors de la connexion au serveur !',
                    life: 5000
                });
            })
        },
        checkCode() {
            if (this.form.verification_code.length == 4) {
                this.sendCode()
            }
        },
        moveToNext(event, index) {
            // Si l'utilisateur entre un chiffre, passer au champ suivant
            if (event.target.value.length === 1 && index < 3) {
                this.$refs[`input${index + 1}`][0].focus();
            }
            // Si backspace est appuyé, revenir au champ précédent
            if (event.inputType === "deleteContentBackward" && index > 0) {
                this.$refs[`input${index - 1}`][0].focus();
            }
        },
        submitCode() {
            const fullCode = this.code.join('');
            if (fullCode.length === 4) {
                alert(`Code soumis : ${fullCode}`);
            } else {
                alert('Veuillez entrer un code valide.');
            }
        },
    }
}
</script>
<style scoped>
.login-section {
    padding-top: 10%;
}

.card-perso {
    top: 50%;
    left: 50%;
    max-width: 350px;
    max-height: 400px;
    width: 90%;
    /* height: 400px; */
    background: #FFFFFF 0% 0% no-repeat padding-box;
    box-shadow: 0px 3px 6px #0000001A;
    border-radius: 16px;
    opacity: 1;
}

.auth-title {
    text-align: center;
    /* font: normal normal normal 18px/180px Altero; */
    letter-spacing: 0.45px;
    color: #0C266C;
    text-transform: uppercase;
    opacity: 1;
}

input:focus {
    box-shadow: 0 0 5px rgba(0, 123, 255, 0.5);
    border-color: #007bff;
}
</style> -->